import type { CostPredictionResponse, OvertimeKind } from "./models";

/**
 * Pure, wage-agnostic helpers that unify how "Predicted Cost" and the OT delta are COMPUTED and RENDERED
 * across every staffing view (desktop grid, approval popup, mobile approve + add). The gold standard is the
 * desktop `NursesMatrixGrid` — these helpers encode exactly its rules so the four views can never drift:
 *
 *  - Cost VALUE  = weeklyCostAfter − weeklyCostBefore (the marginal weekly delta), NOT `shiftCost`.
 *  - Cost STRING = `$X` whole-dollar / `$X.XX` when fractional · `$0` · `-$X[.XX]` (sign before the dollar) ·
 *    `est. $X[.XX]` only when the backend marks the row estimated · `-` (bare dash) when there is no prediction.
 *
 * Deliberately free of React and i18n: the `hideWage` permission gate and the "(+$bonus)" suffix are applied
 * by each view around the returned string, never folded in here. The "est. " / "-" literals match the grid's
 * long-standing output verbatim, so they stay as formatter literals (no i18n keys).
 */

/**
 * The one analytics event for "a cost prediction failed", wherever it ran. Every caller shows an unknown
 * cost ("-" or "not known") instead of guessing, so without the event a backend outage would silently
 * strip the cost and overtime signal from every add and approval with nothing to alert on. `source`
 * says which surface asked; the classic mobile add page predates it and sends none.
 */
export const PREDICT_COST_FAILED_EVENT = "nurse_expected_stats_predict_cost_failed";

export interface FormatPredictedCostOptions {
	/** When true, the backend flagged the figure as an estimate — render `est. $X` (never a negative/$0 variant). */
	isEstimated?: boolean;
}

export function formatPredictedCost(delta: number, { isEstimated }: FormatPredictedCostOptions = {}): string {
	// Round to cents (rounding the *cents* — not `toFixed` on the raw value — keeps a tiny negative
	// delta from rendering as "-$0": Math.round(-0.4)/100 is -0, which is not < 0).
	const rounded = Math.round(delta * 100) / 100;
	const magnitude = Math.abs(rounded);
	// Whole-dollar amounts drop the decimals ("$80"); fractional amounts show cents ("$4.60").
	const amount = Number.isInteger(rounded) ? magnitude.toString() : magnitude.toFixed(2);
	// Estimated figures are only ever produced for positive costs, so the estimated path never flips the sign.
	if (isEstimated) return `est. $${amount}`;
	if (rounded < 0) return `-$${amount}`;
	return `$${amount}`;
}

export interface CostPredictionResult {
	/** Display-ready string (dash when no prediction) — the exact value each view renders. */
	costDisplay: string;
	/** Raw (unrounded) weekly delta backing the string; undefined with no prediction → sorts nulls-last. */
	costValue: number | undefined;
	/** OT delta, forced to 0 for a global-salary nurse; undefined with no prediction. */
	overtimeHours: number | undefined;
	/** DAT-289: which cap produced `overtimeHours`, so callers can name it instead of assuming weekly.
	 * Undefined with no prediction AND when no cap produced the delta — both mean "nothing to name". */
	overtimeType: OvertimeKind | undefined;
	/** Weekly OT before the change (drives the OT cell's before/after tooltip); undefined with no prediction. */
	weeklyOvertimeHoursBeforeChange: number | undefined;
	/** Backend "is this an estimate" flag, passed through so the OT-free views can still render `est.`. */
	isEstimated: boolean | undefined;
}

export interface BuildCostPredictionOptions {
	/** Global-salary nurses accrue no marginal OT, so their OT delta is forced to 0. Required: every caller
	 * knows the nurse, so pass it explicitly (coerce a possibly-undefined source with `Boolean(...)`). */
	isGlobalSalary: boolean;
}

export function buildCostPrediction(response: CostPredictionResponse | undefined, { isGlobalSalary }: BuildCostPredictionOptions): CostPredictionResult {
	if (!response) {
		return {
			costDisplay: "-",
			costValue: undefined,
			overtimeHours: undefined,
			overtimeType: undefined,
			weeklyOvertimeHoursBeforeChange: undefined,
			isEstimated: undefined,
		};
	}
	const delta = response.weeklyCostAfter - response.weeklyCostBefore;
	return {
		costDisplay: formatPredictedCost(delta, { isEstimated: response.isEstimated }),
		costValue: delta,
		overtimeHours: isGlobalSalary ? 0 : response.overtimeHoursDelta,
		overtimeType: response.overtimeType ?? undefined,
		weeklyOvertimeHoursBeforeChange: response.weeklyOvertimeHoursBefore,
		isEstimated: response.isEstimated,
	};
}
